import { t } from '@lingui/core/macro';
import { isPresent } from '@luminovo/commons';

import { getLocale } from '../i18n';

const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 3600;
const SECONDS_IN_DAY = 86400;

const formatterCache = new Map<string, Intl.RelativeTimeFormat>();

/** A cached formatter for `locale`, or for `en` where that isn't supported. */
function getRelativeTimeFormat(locale: string): Intl.RelativeTimeFormat {
    const normalizedLocale = Intl.RelativeTimeFormat.supportedLocalesOf([locale])[0] || 'en';

    if (!formatterCache.has(normalizedLocale)) {
        formatterCache.set(
            normalizedLocale,
            new Intl.RelativeTimeFormat(normalizedLocale, {
                numeric: 'auto',
                style: 'long', // Use long style for better readability
            }),
        );
    }

    return formatterCache.get(normalizedLocale)!;
}

/** The difference in calendar days, not in 24-hour periods. */
function getDaysDifference(date1: Date, date2: Date): number {
    const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());

    return Math.round((d1.getTime() - d2.getTime()) / (SECONDS_IN_DAY * 1000));
}

function roundDiff(value: number): number {
    if (Math.abs(value) < 0.5) {
        return 0;
    }
    return Math.round(value);
}

type DateInput = Date | string | undefined | null;

const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parses values for relative formatting. Date-only ISO strings (YYYY-MM-DD) are treated as
 * local calendar dates so they match HTML `input type="date"` values and avoid UTC midnight
 * shifting the day behind the user's timezone (e.g. "yesterday" for "today" in US Pacific).
 */
function parseDateForRelativeComparison(input: Exclude<DateInput, null | undefined>): Date {
    if (input instanceof Date) {
        return input;
    }

    const trimmed = input.trim();
    const dateOnly = ISO_DATE_ONLY.exec(trimmed);
    if (dateOnly) {
        const y = Number(dateOnly[1]);
        const m = Number(dateOnly[2]) - 1;
        const d = Number(dateOnly[3]);
        return new Date(y, m, d);
    }

    return new Date(trimmed);
}

/**
 * Formats a date relative to now, e.g. "5 minutes ago" or "in 3 days"; `ifAbsent` (default `-`) if none or invalid.
 * A date-only string (YYYY-MM-DD) is formatted in days only, e.g. "today" or "tomorrow".
 */
export function formatRelativeTime(input: DateInput, options?: { ifAbsent?: string }): string {
    if (!isPresent(input)) {
        return options?.ifAbsent ?? '-';
    }

    const today = new Date();
    const date = parseDateForRelativeComparison(input);

    if (isNaN(date.getTime())) {
        return options?.ifAbsent ?? '-';
    }

    // A date without a time is compared by calendar day only: today's date is "today" at any hour.
    if (typeof input === 'string' && ISO_DATE_ONLY.test(input.trim())) {
        return getRelativeTimeFormat(getLocale()).format(getDaysDifference(date, today), 'day');
    }

    const diffTime = date.getTime() - today.getTime();
    const diffSeconds = diffTime / 1000;

    if (Math.abs(diffSeconds) <= 30) {
        if (diffSeconds < 0) {
            return t`a few seconds ago`;
        }
        if (diffSeconds > 0) {
            return t`in a few seconds`;
        }
        return t`just now`;
    }

    // Calendar days, so "yesterday" is the previous date rather than 24 hours ago.
    const diffDays = getDaysDifference(date, today);

    if (diffDays !== 0) {
        const formatter = getRelativeTimeFormat(getLocale());
        return formatter.format(diffDays, 'day');
    }

    const diffHours = roundDiff(diffSeconds / SECONDS_IN_HOUR);
    const diffMinutes = roundDiff(diffSeconds / SECONDS_IN_MINUTE);

    const formatter = getRelativeTimeFormat(getLocale());

    if (Math.abs(diffHours) >= 1) {
        return formatter.format(diffHours, 'hour');
    }

    if (Math.abs(diffMinutes) >= 1) {
        return formatter.format(diffMinutes, 'minute');
    }

    // Unreachable after the 30-second check above; kept as a fallback.
    if (diffSeconds < 0) {
        return t`a few seconds ago`;
    }

    if (diffSeconds > 0) {
        return t`in a few seconds`;
    }

    return t`just now`;
}
